/**
 * Generates a unique, deterministic key for an item in a list.
 *
 * This utility intelligently creates keys based on the item's properties,
 * ensuring the same item always generates the same key while maintaining
 * uniqueness within a list context.
 *
 * @param item - The item to generate a key for
 * @param index - The index of the item in the list (for uniqueness)
 * @returns A unique, deterministic key string
 */
export function generateItemKey<T = unknown>(item: T, index: number): string {
  if (!item) {
    return `item-${index}`;
  }

  if (typeof item !== 'object') {
    return `${String(item)}-${index}`;
  }

  const obj = item as Record<string, unknown>;

  // Priority order for identifier fields
  const identifierFields = [
    'id', // Most common unique identifier
    'key', // Explicit key field
    'name', // Common in benefits, categories, etc.
    'label', // Common in steps, options, etc.
    'title', // Common in content items
    'question', // Common in FAQ items
    'text', // Fallback for text-based items
    'value', // Common in form options
    'index', // Numeric index field
  ];

  // Find the first available identifier field
  let identifier = '';
  for (const field of identifierFields) {
    if (obj[field] != null) {
      identifier = String(obj[field]);
      break;
    }
  }

  // If no identifier found, use object type or generic fallback
  if (!identifier) {
    identifier = obj.constructor?.name || 'item';
  }

  const cleanIdentifier = identifier
    .replace(/[^a-zA-Z0-9\s-_]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase() // Normalize case
    .substring(0, 50); // Limit length

  // Combine identifier with index for uniqueness
  return `${cleanIdentifier}-${index}`;
}
